
import { action, observable, makeObservable, toJS } from "mobx";
import { api } from "../lib/api";
import { SharedStateStore } from "./SharedStateStore";
import { ILivestreamSubscribedResponse, ILivestreamWebinarResponse, ILivestreamCheckStatusResponse, IUnassignedWebinarResponse, ILivestreamParticipantReportResponse, ILivestreamReportResponse } from "../models/apiResponses";
import { ILivestream, IWebinarUnassigned, ILivestreamSubscribed, ILivestreamParticipant, ILivestreamReport } from "../models/apiDataStructures";

const DEFAULT_RECORDS_PER_PAGE = 5
const DEFAULT_PARTICIPANTS_PER_PAGE = 20

export type ILivestreamCurrentView = "today" | "upcoming" | "past" | "new" | "subscribed" | "report";
export type ILivestreamReportFilter = "" | "upcoming" | "archived"

export class LivestreamStore extends SharedStateStore {

    defaultView: ILivestreamCurrentView = "upcoming"

    currentView: ILivestreamCurrentView = this.defaultView;
    webinarsToday: ILivestream[] = [];
    webinarsUpcoming: ILivestream[] = [];
    webinarsArchive: ILivestream[] = [];
    webinarsSubscribed: ILivestreamSubscribed[] = [];
    webinarsUnassigned: IWebinarUnassigned[] = [];
    webinarsReportList: ILivestreamReport[] = [];

    eventsToday = 0;
    eventsSubscribed = 0;
    eventLive = false;
    subscribedEventLive = false;

    firstRender = true;
    loading = true;
    totalPages = 1;
    page = 1;
    keyword = "";

    reportParticipantList: ILivestreamParticipant[] = [];
    reportFilter: ILivestreamReportFilter = "";
    reportKeyword = "";
    reportPage = 1;
    reportTotalPages = 1;
    reportLoading = true;

    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;


    constructor() {
        super()

        makeObservable(this, {
            currentView: observable,
            loading: observable,
            timeZone: observable,

            eventsToday: observable,
            eventsSubscribed: observable,
            eventLive: observable,
            subscribedEventLive: observable,

            webinarsToday: observable,
            webinarsUpcoming: observable,
            webinarsArchive: observable,
            webinarsUnassigned: observable,
            webinarsSubscribed: observable,
            webinarsReportList: observable,

            reportParticipantList: observable,
            reportLoading: observable,

            checkEventsStatus: action,
            switchView: action,
            loadWebinarsUpcoming: action,
            loadWebinarsArchive: action,
            loadWebinarsUnassigned: action,
            loadWebinarsReport: action,
            loadWebinarParticipantsReport: action
        })
    }

    async checkEventsStatus(shouldUpdateView = false) {
        try {
            const url = `livestreams/check?timezone=${this.timeZone}`
            let results = await api.find<ILivestreamCheckStatusResponse>(url)
            this.setState({
                eventsToday: results.eventsToday,
                eventsSubscribed: results.eventsSubscribed,
                eventLive: results.eventLive,
                subscribedEventLive: results.subscribedEventLive 
            })
            if (shouldUpdateView && this.currentView !== "subscribed") {
                if (results.eventsToday > 0) {
                    this.setState({ defaultView: "today", currentView: "today" })
                } else {
                    this.setState({ defaultView: "upcoming", currentView: "upcoming"  })
                }
            }
        } catch (err) {
            throw err;
        }      
    }

    loadViewWebinars(view) {
        switch(view) {
            case "today":
                this.loadWebinarsToday()
                break;
            case "upcoming":
                this.loadWebinarsUpcoming()
                break;
            case "past":
                this.loadWebinarsArchive()
                break;
            case "subscribed":
                this.loadWebinarsSubscribed()
                break;
            case "report":
                this.loadWebinarsReport()
                break;
            default:
                break;
        }
        this.checkEventsStatus()
    }

    switchView(newView: ILivestreamCurrentView) {
        this.setState({
            currentView: newView
        })
        this.resetParams(() => this.loadViewWebinars(newView))
    }

    async loadWebinarsToday(page = 1) {
        this.setState({ loading: true });
        try {
            const url = `livestreams/today?timezone=${this.timeZone}&page=${page}&pageSize=${DEFAULT_RECORDS_PER_PAGE}`;
            let results = await api.find<ILivestreamWebinarResponse>(url)
            let webinars = results.results
            this.setState({
                webinarsToday: webinars,
                page: results.current,
                totalPages: results.totalPages
            })
        } catch (err) {
            throw err;
        } finally {
            this.setState({ loading: false, firstRender: false })
        }      
    }


    async loadWebinarsUpcoming(keyword: string = "", page = 1) {

        this.setState({ loading: true });
        if (keyword) this.keyword = keyword;
    
        try {
            const url = keyword
            ? `livestreams?search=${encodeURIComponent(keyword)}&timezone=${this.timeZone}&page=${page}&pageSize=${DEFAULT_RECORDS_PER_PAGE}`
            : `livestreams?timezone=${this.timeZone}&page=${page}&pageSize=${DEFAULT_RECORDS_PER_PAGE}`
            let results = await api.find<ILivestreamWebinarResponse>(url)
            let webinars = results.results
            this.setState({
                webinarsUpcoming: webinars,
                page: results.current,
                totalPages: results.totalPages
            })
        } catch (err) {
            throw err;
        } finally {
            this.setState({ loading: false, firstRender: false })
        }
    }


    async loadWebinarsReport(keyword: string = "", filter: ILivestreamReportFilter = "", page = 1) {

        this.setState({ loading: true });
        if (keyword) this.keyword = keyword;
    
        try {
            let url = keyword
            ? `livestreams/reports?search=${encodeURIComponent(keyword)}&timezone=${this.timeZone}&page=${page}&pageSize=${DEFAULT_RECORDS_PER_PAGE}`
            : `livestreams/reports?timezone=${this.timeZone}&page=${page}&pageSize=${DEFAULT_RECORDS_PER_PAGE}`
            if (filter === "upcoming") url = url + '&upcoming_only=True'
            if (filter === "archived") url = url + '&archived_only=True'
            let results = await api.find<ILivestreamReportResponse>(url)
            let webinars = results.results
            this.setState({
                webinarsReportList: webinars,
                page: results.current,
                totalPages: results.totalPages
            })
        } catch (err) {
            throw err;
        } finally {
            this.setState({ loading: false, firstRender: false })
        }
    }


    async loadWebinarsSubscribed(keyword: string = "", page = 1) {

        this.setState({ loading: true });
        if (keyword) this.keyword = keyword;

        try {
            const url = `livestreams/subscribed?page=${page}&pageSize=${DEFAULT_RECORDS_PER_PAGE}`;
            let results = await api.find<ILivestreamSubscribedResponse>(url)
            let webinars = results.results
            this.setState({
                webinarsSubscribed: webinars,
                page: results.current,
                totalPages: results.totalPages
            })
        } catch (err) {
            throw err;
        } finally {
            this.setState({ loading: false, firstRender: false })
        }
    }


    async loadWebinarParticipantsReport(webinarID, keyword: string = "", filter = "", usersOnly = "", page = 1, orderBy="name", orderDir="desc") {
        this.setState({ reportLoading: true });
        if (keyword) this.reportKeyword = keyword;
        let participants: ILivestreamParticipant[] = [];
        let ordering = `${orderDir==="asc"?"":"-"}${orderBy}`
        try {
            let url = keyword
            ? `livestream/report/${webinarID}?search=${encodeURIComponent(keyword)}&page=${page}&pageSize=${DEFAULT_PARTICIPANTS_PER_PAGE}&ordering=${ordering}`
            : `livestream/report/${webinarID}?page=${page}&pageSize=${DEFAULT_PARTICIPANTS_PER_PAGE}&ordering=${ordering}`;
            if (filter) {
                let filters = filter.split('&');
                filters.forEach(filter => url = url + `&${filter}_only=True`)
            }
            if (usersOnly === "true") url = url + '&users_only=True'
            let results = await api.find<ILivestreamParticipantReportResponse>(url)
            participants = results.results
            this.setState({
                reportParticipantList: participants,
                reportPage: results.current,
                reportTotalPages: results.totalPages
            })
        } catch (err) {
            throw err;
        } finally {
            this.setState({ reportLoading: false })
        }   
    }

    loadWebinarsUnassigned = async () => {
        try {
            const url = `livestreams/available?timezone=${this.timeZone}`;
            let results = await api.find<IUnassignedWebinarResponse>(url)
            let webinars = results
            this.setState({
                webinarsUnassigned: webinars
            })
        } catch (err) {
            throw err;
        } finally {
        }
    }

    async loadWebinarsArchive(keyword: string = "", page = 1) {
        this.setState({ loading: true });
        try {
            const url = keyword
            ? `livestreams/archive?search=${encodeURIComponent(keyword)}&timezone=${this.timeZone}&page=${page}&pageSize=${DEFAULT_RECORDS_PER_PAGE}`
            : `livestreams/archive?timezone=${this.timeZone}&page=${page}&pageSize=${DEFAULT_RECORDS_PER_PAGE}`
            let results = await api.find<ILivestreamWebinarResponse>(url)
            let webinars = results.results
            this.setState({
                webinarsArchive: webinars,
                page: results.current,
                totalPages: results.totalPages
            })
        } catch (err) {
            throw err;
        } finally {
            this.setState({ loading: false })
        }
    }


    async joinWebinar(meetingId: number | string) {
        try {
            let results = await api.find(`livestream/join/${meetingId}`)
            return results
        } catch (err) {
            throw err;
        }
    }


    async createWebinar(webinar) {
        try {
            await api.create(
                "livestream/",
                webinar
            )
        } catch (err) {
            throw err;
        } finally {
            this.checkEventsStatus();
            this.loadViewWebinars(this.currentView)
        }
    }

    async updateWebinar(webinar) {
        try {
            await api.put(
                `livestream/${webinar.id}`,
                webinar
            );
        } catch (err) {
            throw err
        } finally {
            this.checkEventsStatus();
            this.loadViewWebinars(this.currentView)
        }
    }

    async deleteWebinar(id: number) {
        try {
            await api.delete(`livestream/${id}`);
        } catch (err) {
            throw err
        } finally {
            this.checkEventsStatus();
            this.loadViewWebinars(this.currentView)
        }
    }

    async subscribeToWebinar(id: number): Promise<boolean> {
        if (!id) return false
        try {
            await api.create(
                "livestreams/subscribe",
                {
                    livestream: {
                        id: id
                    }
                }
            )
            return true
        } catch (err) {
            console.error(err)
            return false
        }
    }

    async unsubscribeFromWebinar(id: number): Promise<boolean> {
        if (!id) return false
        try {
            await api.delete(`livestreams/subscribed/${id}`)
            return true
        } catch (err) {
            console.error(err)
            return false
        } 
    }
  
    async getCalendarEvent(id:number) {
        try {
            await api.post(`livestream/calendar/${id}`, {})
        } catch (err) {
            throw err;
        }
    }

    resetParams(callback: Function | undefined = undefined) {
        this.setState({
            loading: true,
            totalPages: 1,
            page: 1,
            keyword: ""
        }, () => callback ? callback() : null)
    }

    getUnassignedWebinarById = (value: number) => {
        let webinars = toJS(this.webinarsUnassigned)
        return webinars.find(item => parseInt(item.meetingID) === value)
    }


}